import { Button } from "@/shared/components/ui/button";
import { Copy } from "lucide-react";
import { v4 } from "uuid";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { useVbsOfferCreateFromOldMutation } from "@/services/backend/vbs/offers/api/offer-api";
import { useEffect, useState } from "react";
import t from "@/lang/lang";
import { useProcessesProcessShowQuery } from "@/services/backend/processes/api/process-api";
import { BranchSelectFrom } from "@/services/backend/branch/components/branch-select-from";
import { ServiceObjectSelectFrom } from "@/services/backend/customers/components/service-object-select-from";

interface Props {
  offerId: string;
  processId: string;
  onCopy: (offerId: string) => void;
}

export function CopyOfferButton({ offerId, processId, onCopy }: Props) {
  const [id, setId] = useState(v4());
  const [copy, { isLoading, error, isSuccess, reset }] =
    useVbsOfferCreateFromOldMutation();
  const { data: process } = useProcessesProcessShowQuery({ id: processId });

  const branches = process?.branches ?? [];
  const serviceObjects = process?.serviceObjects ?? [];
  const [branchId, setBranchId] = useState(branches[0] ?? "");
  const [serviceObjectId, setServiceObjectId] = useState(
    serviceObjects[0] ?? "",
  );

  if (isSuccess) {
    onCopy(id);
    reset();
    setId(v4());
  }

  const disabled =
    isLoading ||
    !process ||
    !process.customerId ||
    branchId === "" ||
    serviceObjectId === "";

  const { toast } = useToast();

  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error, false),
        variant: "destructive",
      });
      reset();
    }
  }, [error, toast, reset]);

  const onClick = () => {
    if (disabled) {
      return;
    }

    copy({
      oldOfferId: offerId,
      id,
      processId: process.id,
      companyId: process.companyId,
      branchId,
      customerId: process.customerId!,
      serviceObjectId,
    });
  };

  return (
    <div className="flex flex-col items-end gap-2">
      {branches.length !== 1 && (
        <BranchSelectFrom
          branchIds={branches}
          value={branchId}
          onValueChange={setBranchId}
        />
      )}
      {serviceObjects.length !== 1 && (
        <ServiceObjectSelectFrom
          serviceObjectIds={serviceObjects}
          customerId={process?.customerId}
          value={serviceObjectId}
          onValueChange={setServiceObjectId}
        />
      )}
      <Button onClick={onClick} size="sm" disabled={disabled}>
        <Copy className="mr-2 h-4 w-4" />
        <span>{t("Daten übernehmen")}</span>
      </Button>
    </div>
  );
}
